import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf,
  ShieldCheck,
  CalendarClock,
  Camera,
  ArrowRight,
  Star,
  Sparkles,
  Check,
  Truck,
} from "lucide-react";
import QuoteWidget from "@/components/QuoteWidget";
import CustomPlanBanner from "@/components/CustomPlanBanner";
import TestimonialCarousel, {
  TestimonialCarouselHeader,
} from "@/components/TestimonialCarousel";
import { api } from "@/lib/api";

const PLAN_PREVIEW = [
  {
    code: "monthly",
    name: "Monthly",
    price: "$25",
    cadence: "/ month",
    desc: "Up to 2 bins · 1 cleaning per month",
    perks: ["Eco-safe soap", "Sanitizing rinse", "Photo proof", "$8 per additional bin"],
    badge: null,
  },
  {
    code: "biweekly",
    name: "Bi-Weekly",
    price: "$40",
    cadence: "/ month",
    desc: "Includes 2 bins · 2 cleanings per month",
    perks: [
      "Eco-safe soap",
      "Sanitizing rinse",
      "Priority scheduling",
      "Photo proof",
      "$8 per additional bin",
    ],
    badge: "Most Popular",
  },
  {
    code: "onetime",
    name: "One-Time",
    price: "$40",
    cadence: "/ bin",
    desc: "Single deep clean · try us first",
    perks: ["Eco-safe soap", "Sanitizing rinse", "Photo proof", "$8 per additional bin"],
    badge: null,
  },
];

const ICON_MAP = {
  leaf: Leaf,
  shield: ShieldCheck,
  calendar: CalendarClock,
  camera: Camera,
  sparkles: Sparkles,
  truck: Truck,
};

const VALUE_CARD_FALLBACK = [
  {
    id: "fallback-eco",
    slug: "eco-friendly",
    title: "Eco-Friendly Cleaning",
    body: "100% biodegradable solutions that protect your property, your kids, and the environment.",
    icon: "leaf",
    image_url:
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1600&auto=format&fit=crop",
  },
  {
    id: "fallback-odor",
    slug: "odor-bacteria",
    title: "Odor and Bacteria Elimination",
    body: "High-pressure sanitizing rinse that kills 99.9% of odor-causing bacteria.",
    icon: "shield",
    image_url:
      "https://images.unsplash.com/photo-1583907659441-addbe699e921?q=80&w=1600&auto=format&fit=crop",
  },
  {
    id: "fallback-sched",
    slug: "scheduled-reliable",
    title: "Scheduled and Reliable",
    body: "We sync with your local trash pickup. Set it once and forget it forever.",
    icon: "calendar",
    image_url:
      "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?q=80&w=1600&auto=format&fit=crop",
  },
  {
    id: "fallback-photo",
    slug: "photo-proof",
    title: "Photo Proof Every Clean",
    body: "After every wash you get a photo and a fresh receipt. No surprises.",
    icon: "camera",
    image_url:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=1600&auto=format&fit=crop",
  },
];

export default function Home() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  return (
    <div data-testid="page-home" className="bg-white overflow-x-clip">
      {/* HERO. Full-bleed video band with overlaid headline + Quote widget */}
      <section
        className="relative isolate overflow-hidden min-h-[620px] md:min-h-[660px] lg:min-h-[640px] flex items-center"
        data-testid="hero-section"
      >
        {/* Background visual: poster (reduced-motion) or autoplaying loop */}
        {reducedMotion ? (
          <img
            src="/hero-poster.jpg"
            alt=""
            aria-hidden="true"
            className="hero-video absolute inset-0 w-full h-full object-cover"
            data-testid="hero-poster-static"
          />
        ) : (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
            aria-hidden="true"
            tabIndex={-1}
            className="hero-video absolute inset-0 w-full h-full object-cover pointer-events-none"
            data-testid="hero-video"
          >
            <source src="/hero-loop.mp4" type="video/mp4" />
          </video>
        )}

        {/* Mobile/tablet. Softer vertical gradient: text legible at top, video shines through middle, quote widget readable at bottom */}
        <div
          aria-hidden="true"
          className="lg:hidden absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/40"
        />
        {/* Desktop. Left-side darkener for headline legibility; right side shows the video cleanly */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent"
        />
        {/* Bottom fade so the hero bleeds into the white trust strip below */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-24 lg:h-28 bg-gradient-to-b from-transparent to-white"
        />

        <div className="container-page relative z-10 w-full grid lg:grid-cols-12 gap-10 lg:gap-12 items-center pt-12 pb-20 md:pt-16 md:pb-24 lg:pt-20 lg:pb-28">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/12 backdrop-blur border border-white/30 rounded-full px-3 py-1.5 mb-6 shadow-soft">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                Premium South Jersey Service
              </span>
            </div>

            <h1 className="font-heading h1-fluid font-extrabold text-white text-balance break-words drop-shadow-[0_2px_28px_rgba(0,0,0,0.45)]">
              Fresh. Clean.{" "}
              <span className="text-brand-400">Silky Smooth.</span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-white/90 max-w-xl leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.35)]">
              Premium trash bin cleaning in South Jersey. We pick up where the
              trash truck leaves off. Bins washed, sanitized, deodorized, and
              photographed.
            </p>

            <div className="mt-7 hidden md:flex flex-wrap items-center gap-4">
              <Link to="/plans" className="btn-primary" data-testid="hero-cta-plans">
                Get My Bins Cleaned
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-xl bg-white/10 backdrop-blur border-2 border-white/45 text-white font-semibold px-6 sm:px-7 py-3 text-sm tracking-wide transition-[background-color,border-color,transform] duration-200 hover:bg-white/20 hover:border-white/70 hover:-translate-y-0.5 active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-white/25"
                data-testid="hero-cta-how"
              >
                How It Works
              </Link>
            </div>

            <div className="mt-9">
              <QuoteWidget />
            </div>

            <div className="mt-7 flex items-center gap-4 text-sm text-white/85 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
              <div className="flex items-center gap-1 text-gold">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" stroke="none" />
                ))}
              </div>
              <span>
                Loved by neighbors in Pine Hill and nearby South Jersey communities
              </span>
            </div>
          </div>

          {/* Right column intentionally empty on desktop. The video IS the visual */}
          <div className="hidden lg:block lg:col-span-5" aria-hidden="true" />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section
        className="bg-white border-y border-slate-100"
        data-testid="trust-strip"
      >
        <div className="container-page py-10 md:py-14 text-center">
          <div data-reveal className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-medium">
              Serving
            </p>
            <p className="text-sm md:text-[15px] font-semibold text-slate-700 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span>Pine Hill and nearby South Jersey communities</span>
            </p>
          </div>
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section
        className="section-padding bg-brand-50/40"
        data-testid="plans-preview-section"
      >
        <div className="container-page">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4">Plans &amp; Pricing</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ink text-balance">
              Honest pricing. <span className="text-brand">No surprises.</span>
            </h2>
            <p className="mt-4 text-base text-slate-600 max-w-xl">
              Pick a plan that fits your home. Cancel or switch anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {PLAN_PREVIEW.map((p, idx) => (
              <div
                key={p.code}
                data-reveal
                style={{ "--reveal-delay": `${idx * 80}ms` }}
                className={`relative rounded-2xl bg-white p-6 sm:p-7 lg:p-8 transition-all duration-300 ${
                  p.badge
                    ? "border border-slate-100 border-l-[6px] border-l-gold shadow-lift md:-translate-y-2"
                    : "border border-slate-100 shadow-soft hover:-translate-y-1 hover:shadow-lift"
                }`}
                data-testid={`plan-preview-${p.code}`}
              >
                {p.badge && (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow-soft">
                    <Sparkles size={11} /> {p.badge}
                  </span>
                )}
                <h3 className="font-heading text-2xl font-bold text-ink mb-1">
                  {p.name}
                </h3>
                <p className="text-sm text-slate-500 mb-6">{p.desc}</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-heading text-5xl font-extrabold text-ink">
                    {p.price}
                  </span>
                  <span className="text-sm text-slate-500">{p.cadence}</span>
                </div>
                <ul className="space-y-2.5 mb-7 text-sm text-slate-700">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2.5">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                        <Check size={12} />
                      </span>
                      {perk}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/plans"
                  className={p.badge ? "btn-primary w-full" : "btn-secondary w-full"}
                  data-testid={`plan-preview-cta-${p.code}`}
                >
                  See Details
                </Link>
              </div>
            ))}
          </div>

          <CustomPlanBanner compact eyebrow="Flexible Options" />
        </div>
      </section>

      {/* WHY SILKY SMOOTH */}
      <section
        className="section-padding bg-white"
        data-testid="features-section"
      >
        <div className="container-page">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4">Why Silky Smooth</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ink text-balance">
              Built for homes that{" "}
              <span className="text-brand">deserve better.</span>
            </h2>
          </div>

          <ValuePropGrid />
        </div>
      </section>

      {/* TESTIMONIAL CAROUSEL */}
      <section
        className="section-padding bg-brand-50/40"
        data-testid="testimonial-teaser"
      >
        <div className="container-page">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <TestimonialCarouselHeader />
            </div>
            <div className="lg:col-span-7">
              <TestimonialCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="section-padding bg-brand"
        data-testid="final-cta-section"
      >
        <div className="container-page text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80 mb-4">
            Ready when you are
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white text-balance max-w-3xl mx-auto">
            Ready for fresh bins?
          </h2>
          <p className="mt-5 text-white/85 max-w-xl mx-auto text-lg">
            Sign up in 90 seconds. We'll handle the rest.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-white text-brand-700 font-semibold rounded-xl px-7 py-3 text-sm tracking-wide transition-all duration-200 hover:bg-brand-50 hover:-translate-y-0.5 hover:shadow-lift"
              data-testid="final-cta-signup"
            >
              Sign Up Now
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/plans"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-semibold rounded-xl px-7 py-3 text-sm tracking-wide transition-all duration-200 hover:border-white hover:bg-white/10"
              data-testid="final-cta-plans"
            >
              Browse Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ValuePropGrid() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/value-props")
      .then(({ data }) => {
        if (!cancelled)
          setItems(data.items && data.items.length ? data.items : VALUE_CARD_FALLBACK);
      })
      .catch(() => {
        if (!cancelled) setItems(VALUE_CARD_FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const list = items || VALUE_CARD_FALLBACK;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-8">
        {list.map((v, i) => {
          const Icon = ICON_MAP[v.icon] || Sparkles;
          return (
            <article
              key={v.id || v.slug}
              data-reveal
              style={{ "--reveal-delay": `${i * 80}ms` }}
              className="card-shimmer group relative overflow-hidden rounded-2xl min-h-[170px] lg:min-h-[340px] flex flex-col justify-end text-white p-4 sm:p-5 lg:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              data-testid={`value-card-${i}`}
              data-slug={v.slug}
            >
              <div className="absolute inset-0 bg-slate-900" aria-hidden="true" />
              {v.image_url && (
                <img
                  src={v.image_url}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-slate-900/20 transition-opacity duration-500 group-hover:from-slate-900/92"
              />
              <div className="relative">
                <div className="h-8 w-8 lg:h-12 lg:w-12 rounded-lg lg:rounded-xl bg-white/15 backdrop-blur text-white flex items-center justify-center mb-2.5 lg:mb-5 border border-white/20 shadow-soft">
                  <Icon className="h-4 w-4 lg:h-[22px] lg:w-[22px]" />
                </div>
                <h3 className="font-heading text-[15px] lg:text-2xl font-bold text-white mb-1 lg:mb-2 leading-tight drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)]">
                  {v.title}
                </h3>
                <p className="text-[12px] lg:text-sm text-slate-100/90 leading-snug lg:leading-relaxed line-clamp-2 lg:line-clamp-none">
                  {v.body}
                </p>
              </div>
            </article>
          );
        })}
      </div>
      <style>{`
        @keyframes card-shimmer {
          0%   { transform: translateX(-150%) skewX(-12deg); }
          80%  { transform: translateX(150%) skewX(-12deg); }
          100% { transform: translateX(150%) skewX(-12deg); }
        }
        .card-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(100deg,
            transparent 25%,
            rgba(255,255,255,0.22) 48%,
            rgba(255,255,255,0.38) 50%,
            rgba(255,255,255,0.22) 52%,
            transparent 75%);
          transform: translateX(-150%) skewX(-12deg);
          animation: card-shimmer 1s ease-in-out infinite;
          pointer-events: none;
          mix-blend-mode: screen;
          z-index: 1;
        }
        @media (min-width: 1024px) {
          .card-shimmer::after { animation: none; content: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .card-shimmer::after { animation: none; content: none; }
        }
      `}</style>
    </>
  );
}
